<template>
   <div class="container py-5 h-100">
    <div class="row d-flex justify-content-center align-items-center h-100">
      <div class="col-12 col-md-8 col-lg-6 col-xl-5">
        <div class="card text-black" style="border-radius: 1rem;">
          <div class="card-body p-5 text-center">
            <div class="card-body text-center">
              <div>
                <img :src="require('@/assets/img/product/core.png')" class="img-fluid" />
              </div>
              <div>
                <div class="spinner-border text-info" role="status">
                  <span class="visually-hidden">Loading...</span>
                </div>
                <p class="text-muted">verifying access..</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import jwt_decode from 'jwt-decode'

  export default {
    name: 'Validation',

    data() {
      return {
        hasTraining: false,
        hasPhishing: false,
        hasPentest: false,
        isVertexAnalyst: null,
        organisations: [],
        isSuperAdmin: false,
      }
    },

    async created() {
      await this.validate()
    },

    methods: {
      async validate() {
        // Exit if no token is found
        const access_token = this.$route.query.access_token;
        if (!access_token) {
          this.$store.dispatch('auth/logout');
          return;
        }

        // Decode the token
        let decoded = null;
        try {
          decoded = jwt_decode(access_token);
        } catch (error) {
          console.error("Invalid token:", error);
          this.$store.dispatch('auth/logout');
          return;
        }

        this.$store.commit("auth/setAccessToken", access_token);
        this.$store.commit("auth/setJwtPayload", decoded);
        this.$store.commit("auth/authenticated", true);

        //set product access after jwt decode
        this.setProductAccess()

        // Check roles and set login loop
        this.setLoginLoop(decoded);

        // Check if user has required roles
        if (!this.hasValidRoles(decoded)) {
          this.$toast.error("User has no valid role. Please contact administrator.");
          this.$store.dispatch('auth/logout');
          return;
        }

        //set superadmin flag
        this.isSuperAdmin = this.$store.state.auth.isSuperAdmin

        // Set isVertexAnalyst flag
        this.isVertexAnalyst = this.$store.state.auth.accessType === "vertex_analyst"

        // Fetch organizations in parallel only when needed
        if (this.isSuperAdmin || this.isVertexAnalyst) {
          await this.fetchOrganisations();
          this.handleSuperAdminAndAnalyst(decoded);
        } else {
          await this.handleNonAdmin(decoded);
        }

        //set active products
        let products = await this.getProducts(this.$helper.setOrganisationID())

        this.$store.commit("auth/setOrgProducts", products);


        // Redirect based on recent URL or roles
        this.redirectToAppropriatePage();
      },

      // Check if user has valid roles
      hasValidRoles(decoded) {
        return this.$store.state.auth.isSuperAdmin || decoded.organisation_role;
      },

      // Set login loop based on query
      setLoginLoop() {
        const loginLoop = this.$route.query.login_loop || 0;
        this.$store.commit("auth/setLoginLoop", loginLoop);
      },

      // Fetch organizations if not already available
      async fetchOrganisations() {
        if (!this.organisations.length) {
          this.organisations = await this.getOrganisations();
        }
      },

      // Handle logic for super admin and vertex analyst
      async handleSuperAdminAndAnalyst(decoded) {
        const orgIdFromQuery = this.$route.query.org_id;
        let organisationToSet;

        if (orgIdFromQuery) {
          organisationToSet = this.organisations.find(org => org.id == orgIdFromQuery);
        }

        if (organisationToSet) {
          this.$store.commit("user/setOrganisation", organisationToSet);
        } else if (this.isVertexAnalyst) {
          const organisation = this.organisations.find(org => org.id === decoded.organisation_role[0].organisation_id);
          if (organisation) {
            organisation.products = [...(organisation.products || []), decoded.organisation_role[0].products];
            this.$store.commit("user/setOrganisation", organisation);
            localStorage.setItem('userOrganisation', JSON.stringify({ name: organisation.name, id: organisation.id }));
          }
        } else if (!this.isVertexAnalyst) { // Super Admin fallback
          // If no org_id is provided, default to the first organisation.
          this.$store.commit("user/setOrganisation", this.organisations[0]);
        }

        this.$store.commit("user/setOrganisations", this.organisations);
      },

      // Handle logic for non-admin roles
      async handleNonAdmin(decoded) {
        const organisation = await this.getOrganisation(decoded.organisation_role[0].organisation_id);

        this.$store.commit("user/setBambooState", Boolean(organisation.bamboo_hr_api_key));

        if (decoded.organisation_role) delete decoded.organisation_role[0].organisation_logo;

        this.$store.commit("user/setOrganisation", decoded.organisation_role[0]);

        if (organisation.org_status === "Active" || organisation.org_status === "active") {
          if (this.hasTraining) {
            await this.getUserVideos(decoded.user_id, decoded.organisation_role[0].organisation_id);
            if (this.firstLogin) {
              await this.$axios.post(`/trainings/users/${decoded.user_id}/organisations/${decoded.organisation_role[0].organisation_id}/assign-video`);
            }
          }
        }
      },

      // Redirect based on role or recent URL
      redirectToAppropriatePage() {
        const isAdmin = this.isSuperAdmin || this.isCoreAdmin(this.$store.state.user.organisation.products, 'admin') || this.isVertexAnalyst;
        const recentUrl = localStorage.getItem('currentUrl');

        if (recentUrl) {
          // Handle the case when a recent URL exists
          if (recentUrl.includes('/dashboard')) {
            // If the recent URL is '/dashboard', check if the user is not an admin and not a manager
            if (!isAdmin && !this.$store.state.auth.isManager) {
              // Redirect to the "else" path if user is not an admin or a manager
              this.$router.push('/training/assigned-training');
            } else {
              // Otherwise, allow access to the dashboard (admin or manager)
              this.$router.push(recentUrl);
              return
            }
          } else {
            // If the recent URL is not '/dashboard', just redirect to it as usual
            this.$router.push(recentUrl);
            return
          }

        }

        // Handle navigation for super admin
        if (this.isSuperAdmin) {
          this.$router.push('/dashboard');
          return;
        }

        // Handle navigation for admin
        if (isAdmin) {
          if (!this.hasTraining && this.hasPhishing) {
            this.$router.push('/phishing/campaign-settings');
          } else {
            this.$router.push('/dashboard');
          }
          return;
        }

        // Handle navigation for manager or general user
        if (this.$store.state.auth.isManager && this.$store.state.auth.isGeneralUser) {
          this.$router.push('/dashboard');
          return;
        }

        // Default redirection for all other cases
        this.$router.push('/training/assigned-training');
      },


      // Get organisations from API
      async getOrganisations() {
        try {
          const response = await this.$axios.get(`/supadmin/organisations`);
          return response.data.success ? response.data.organisations : [];
        } catch (error) {
          this.$toast.error('Failed to load organisations.');
          return [];
        }
      },

      // Get organisation from API
      async getOrganisation(id) {
        try {
          const response = await this.$axios.get(`/organisations/${id}/validation`);
          return response.data.success ? response.data.organisation : {};
        } catch (error) {
          this.$toast.error('Failed to load organisation.');
          return {};
        }
      },

      // Get user videos from API
      async getUserVideos(userID, orgID) {
        try {
          const response = await this.$axios.get(`/trainings/users/${userID}/organisations/${orgID}/videos`);
          this.firstLogin = response.data.training_videos.length === 0;
        } catch (error) {
          this.$toast.error('Failed to load training videos.');
        }
      },

      async getProducts(orgID) {
        try {
          const response = await this.$axios.get(`/organisations/${orgID}/products`);
          if (response.data.success) {
            return response.data.products; // Correct return value
          } else {
            this.$toast.error(response.data.message);
            return null; // Ensure a return value in case of failure
          }
        } catch (error) {
          this.$toast.error("Failed to fetch products");
          console.error(error);
          return null;
        }
      },

      // Core admin check
      isCoreAdmin(products, role){
        var isExist = false
        products.forEach(product => {
          if (product.product == "email_compromise_check" || product.product == "cyber_security_online_awareness_training" || product.product == "automated_test_phishing_campaigns") {
            if (product.roles.includes(role)) isExist = true
          }
        });

        return isExist
      },

      filterOrganisations(organisations){
        if (!this.$route.query.org_selected){
          this.$store.commit("user/setOrganisation", organisations[0]);
          return
        }

        var orgIndex = -1
        for (let index = 0; index < organisations.length; index++) {
          if (organisations[index].id == this.$route.query.org_selected){
            orgIndex = index
            break;
          }
        }

        if (orgIndex != -1) {
          //delete organisation_logo
          delete organisations[orgIndex].organisation_logo

          this.$store.commit("user/setOrganisation", organisations[orgIndex]);
        } else {
          //delete organisation_logo
          delete organisations[0].organisation_logo

          this.$store.commit("user/setOrganisation", organisations[0]);
        }
      },

      setProductAccess(){
        this.hasPentest = this.$store.state.auth.hasPentest;
        this.hasTraining = this.$store.state.auth.hasTraining;
        this.hasPhishing = this.$store.state.auth.hasPhishing;
      }
    },

    // watch: {
    //   '$route': {
    //     immediate: true,
    //     handler() {
    //       this.validate();
    //     }
    //   }
    // },
  }
</script>
